<template>
  <n-card
    :hoverable="true"
    content-style="padding: 0;"
    :theme-overrides="cardThemeOverrides"
  >
    <div class="image-container">
      <img
        v-if="imageSrc"
        :src="imageSrc"
        :alt="imageAlt"
      />
      <div
        v-if="badges.length > 0"
        class="badges"
      >
        <span
          v-for="badge in badges"
          :key="badge.id"
          class="badge"
        >
          {{ badge }}
        </span>
      </div>
    </div>
    <div class="text-container">
      <div class="information">
        <!-- Title -->
        <div v-if="title">
          <h2>{{ title }}</h2>
        </div>
        <!-- adresse STEP 1 SPA -->
        <div
          v-if="address"
          class="address-wrapper"
        >
          <address>
            <a
              v-if="linkMap"
              :href="linkMap"
              target="_blank"
              >{{ address }}</a
            >
          </address>
        </div>
        <!-- Durée - prix min STEP 1 SPA -->
        <div
          v-if="item.minPrice"
          class="info-treatment"
        >
          <span
            v-if="durations && journeyType === 'center'"
            class="duration"
          >
            {{ formattedDurations }}
          </span>
          <span
            v-if="durations && minPrice && journeyType === 'center'"
            class="separator"
            >|</span
          >
          <span
            v-if="minPrice && journeyType === 'center'"
            class="price"
            >{{ translationsStore.t("from_price") }} {{ minPrice }}
            {{ currencyCode }}</span
          >
        </div>
        <!-- horaires STEP 1 SPA -->
        <div
          v-if="openingHours"
          class="opening-hours"
        >
          <strong>{{ translationsStore.t("opening_hours") }}</strong>
          <p style="white-space: pre-line">{{ openingHours }}</p>
        </div>
      </div>
      <div
        v-if="journeyType === 'center'"
        class="button-wrapper"
      >
        <!-- Optional external link STEP 1 SPA -->
        <a
          v-if="moreInfoUrl"
          :href="moreInfoUrl"
          class="more-info"
          target="_blank"
          >{{ translationsStore.t("more_info") }}</a
        >
        <AnimatedButton
          :label="translationsStore.t('booking_item_cta')"
          @click="$emit('selectItem', type, item, bookingStore.currentCenter)"
        />
      </div>
      <!-- STEP 1 INFO SPAS PARCOURS SOIN -->
      <div
        v-if="journeyType === 'service'"
        class="spas-info"
      >
        <div
          v-for="center in centersForService"
          :key="center.id"
          class="spa-item"
        >
          <h3>{{ center.title }}</h3>
          <div
            v-if="item.minPrice"
            class="info-treatment"
          >
            <span
              v-if="durations"
              class="duration"
            >
              {{ formattedDurations }}
            </span>
            <span
              v-if="durations && minPrice"
              class="separator"
              >|</span
            >
            <span
              v-if="minPrice"
              class="price"
              >{{ translationsStore.t("from_price") }} {{ minPrice }}
              {{ currencyCode }}</span
            >
          </div>
          <AnimatedButton
            :label="translationsStore.t('booking_item_cta')"
            @click="$emit('selectItem', type, item, center)"
          />
        </div>
      </div>
    </div>
  </n-card>
</template>

<script setup>
import AnimatedButton from "../AnimatedButton.vue";
import { computed, ref } from "vue";
import { useTranslationsStore } from "../../stores/translations";
import { useBookingStore } from "../../stores/booking";

const translationsStore = useTranslationsStore();
const bookingStore = useBookingStore();
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  type: {
    type: String,
    required: true,
  },
});
defineEmits(["selectItem"]);
const imageSrc = computed(
  () => props.item.visuel_spa_booking?.url || props.item.image?.url || ""
);
const imageAlt = computed(
  () =>
    props.item.visuel_spa_booking?.altText || props.item.image?.altText || ""
);
const title = computed(
  () => props.item["nom_du_spa"] || props.item["title"] || ""
);
const address = computed(
  () => props.item["adresse_postale_booking"] || props.item["address"] || ""
);
const openingHours = computed(
  () => props.item["horaires_d_ouverture_booking"] || ""
);
const linkMap = computed(() => props.item["lien_map"] || "");
const moreInfoUrl = computed(
  () => props.item.reference_center_booking?.onlineStoreUrl || ""
);
const badges = computed(() => props.item.badges || []);
const minPrice = computed(() => {
  const price = props.item.minPrice || "";
  return price ? Number(price).toFixed(price % 1 === 0 ? 0 : 2) : "";
});
const currencyCode = computed(() => props.item.currencyCode?.value || "€");
const centersForService = computed(() => {
  if (props.type !== "service") return [];
  if (bookingStore.currentCenter?.id && props.item.centers.length === 1) {
    return [bookingStore.currentCenter];
  }

  // If nothing selected, return all centers
  return props.item.centers || [];
});
const journeyType = computed(() => {
  return bookingStore.journeyType;
});
const durations = ref(null);
const formattedDurations = ref(null);
if (props.item.duration) {
  durations.value = props.item.duration.references.edges.map(
    (edge) => edge.node.fields.find((field) => field.key === "duration").value
  );
  formattedDurations.value = durations.value.join(", ");
}
const cardThemeOverrides = {
  fontSizeMedium: "0.8125rem", // Content font size
};
</script>

<style scoped>
/* Optional styling */
.n-card {
  border: none;
  border-bottom: 1px solid rgba(0 0 0 / 10%);
  background: rgb(var(--brand-white) / 1);
  font-size: 0.8125rem;
}
.n-card:not(:last-child) {
  margin-bottom: 1.5rem;
}
.n-card:hover {
  box-shadow: none;
}
.n-card > .n-card__content {
  padding-inline: 0;
}
.n-card .button-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.n-card .button-wrapper a {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  text-decoration: underline;
}
.n-card button {
  display: block;
}
.n-card .button-wrapper a:hover,
.n-card .button-wrapper a:focus,
.n-card .button-wrapper a:active {
  text-decoration: none;
}
.n-card .text-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem 16px 1.5rem;
}
.n-card h2,
.n-card p {
  margin: 0;
}
.n-card h2 {
  font: 1.5rem/1 "nimbus-roman";
  font-weight: 325;
}
.n-card .address-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-block-start: 0.75rem;
  margin-block-end: 1.5rem;
}
.n-card .opening-hours strong {
  display: block;
  margin-block-end: 0.75rem;
}
.n-card .icon {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  margin-inline-end: 0.25rem;
}
.n-card .icon svg {
  width: 100%;
  height: 100%;
}
.n-card address {
  font-style: normal;
}
.n-card .image-container {
  position: relative;
}
.n-card .image-container img {
  width: 100%;
  height: 100%;
  max-height: 16.3125rem;
  object-fit: cover;
}
.n-card .badges {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 0.25rem;
  inset-block-start: 1rem;
  inset-inline-end: 1rem;
}
.n-card .badge {
  display: inline-block;
  background-color: rgb(var(--brand-white) / 1);
  padding: 0.25rem 8px;
  font-size: 0.6rem;
  line-height: 1.2;
  font-weight: 500;
  letter-spacing: 0.2px;
  text-transform: uppercase;
}
.n-card .more-info {
  color: rgb(var(--brand-black) / 1);
}
.n-card .spa-item {
  background-color: rgb(var(--brand-neutral-2) / 1);
  padding: 1rem 16px;
}
.n-card .spa-item + .spa-item {
  margin-top: 1rem;
}
.n-card h3 {
  margin: 0;
  font-family: var(--text-font-family);
  font-weight: 600;
}
.spa-item .marquee-button {
  margin-top: 1.5rem;
}
.duration,
.price,
.info-treatment {
  font-size: 1rem;
}
.separator {
  padding-inline: 1rem;
}
@media screen and (min-width: 1000px) {
  .n-card {
    padding-block-end: 1rem;
  }
  .n-card:not(:last-child) {
    margin-block-end: 3rem;
  }
  :deep(.n-card__content) {
    display: flex;
    gap: 2rem;
    padding-block-end: 1rem;
  }
  .n-card .image-container {
    flex: 0 0 auto;
    width: 13.125rem;
    height: 13.125rem;
    min-height: 13.125rem;
    aspect-ratio: 1/1;
  }
  .n-card .image-container img {
    width: 100%;
    height: 100%;
    max-height: 100%;
  }
  .n-card .text-container {
    width: 100%;
    gap: 1.25rem;
    padding: 0;
  }
  .n-card .address-wrapper {
    margin-block-start: 1rem;
  }
  .n-card h2 {
    font-size: 1.875rem;
  }
}
</style>
